import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    GraduationCap, Calendar as CalendarIcon, Users, 
    Clock, CheckCircle, TrendingUp, ShieldAlert, Award
} from 'lucide-react';
import StudentsPanel from './StudentsPanel';
import CalendarPanel from './CalendarPanel';
import { getProfile } from '../../lib/rbac';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

export default function AttendanceManagerPortal() {
    const [activeTab, setActiveTab] = useState('students');
    const [stats, setStats] = useState({
        totalStudents: 0,
        averageAttendance: 0,
        riskCount: 0,
        todayClasses: 0
    });
    const [loadingStats, setLoadingStats] = useState(true);
    const profile = getProfile();

    useEffect(() => {
        const fetchDashboardStats = async () => {
            if (!profile?.team_id) return;
            setLoadingStats(true);
            try {
                // Fetch students count, average attendance, and risk counts directly
                const { data: students, error: studentsError } = await supabase
                    .from('students')
                    .select('id, attendance_pct, overall_score')
                    .eq('team_id', profile.team_id);

                if (studentsError) throw studentsError;

                const { count: classesCount, error: classesError } = await supabase
                    .from('scheduled_events')
                    .select('*', { count: 'exact', head: true })
                    .eq('team_id', profile.team_id)
                    .gte('start_time', new Date().toISOString().split('T')[0] + 'T00:00:00')
                    .lte('start_time', new Date().toISOString().split('T')[0] + 'T23:59:59');

                if (classesError) throw classesError;

                const total = students?.length || 0;
                let avgAtt = 0;
                let risk = 0;

                if (total > 0) {
                    const totalAtt = students.reduce((sum, s) => sum + (s.attendance_pct || 0), 0);
                    avgAtt = Math.round(totalAtt / total);
                    risk = students.filter(s => (s.attendance_pct || 100) < 75).length;
                }

                setStats({
                    totalStudents: total,
                    averageAttendance: avgAtt,
                    riskCount: risk,
                    todayClasses: classesCount || 0
                });
            } catch (err) {
                console.error('Error loading manager dashboard stats:', err);
                toast.error('Failed to load dashboard metrics');
            } finally {
                setLoadingStats(false);
            }
        };

        fetchDashboardStats();
    }, [profile?.team_id]);

    const tabs = [
        { id: 'students', label: 'Students & Attendance', icon: GraduationCap },
        { id: 'calendar', label: 'Class Scheduler', icon: CalendarIcon }
    ];

    return (
        <div className="flex flex-col h-full bg-zinc-50/50 dark:bg-zinc-950/30 overflow-hidden">
            {/* Header & Stats Banner */}
            <div className="p-6 pb-4 bg-white dark:bg-zinc-900/40 border-b border-zinc-200 dark:border-zinc-800 backdrop-blur-xl">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 uppercase tracking-wider">
                                Manager Portal
                            </span>
                            <span className="text-xs text-zinc-500 font-mono">Level 7 RBAC</span>
                        </div>
                        <h1 className="text-2xl font-black tracking-tight text-zinc-900 dark:text-zinc-50">
                            Attendance Management Hub
                        </h1>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                            Real-time session oversight, student attendance logs, and scheduling coordinates.
                        </p>
                    </div>

                    {/* Navigation Tab Pills */}
                    <div className="flex items-center p-1 rounded-xl bg-zinc-100 dark:bg-zinc-800/80 border border-zinc-200/50 dark:border-zinc-700/30 w-fit">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                                        isActive 
                                            ? 'text-white shadow-md shadow-blue-500/10' 
                                            : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-200'
                                    }`}
                                >
                                    {isActive && (
                                        <motion.div 
                                            layoutId="activePortalTab"
                                            className="absolute inset-0 bg-blue-600 rounded-lg -z-0"
                                            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                                        />
                                    )}
                                    <Icon className="w-3.5 h-3.5 relative z-10" />
                                    <span className="relative z-10">{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Dashboard KPI cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Card 1 */}
                    <div className="p-4 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 shadow-sm flex items-center gap-4 transition-all hover:border-zinc-300 dark:hover:border-zinc-700">
                        <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
                            <Users className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 leading-none mb-1">Total Enrolled</p>
                            <h3 className="text-lg font-black tracking-tight text-zinc-800 dark:text-zinc-100 leading-none">
                                {loadingStats ? '...' : stats.totalStudents}
                            </h3>
                        </div>
                    </div>

                    {/* Card 2 */}
                    <div className="p-4 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 shadow-sm flex items-center gap-4 transition-all hover:border-zinc-300 dark:hover:border-zinc-700">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                            <CheckCircle className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 leading-none mb-1">Avg Attendance</p>
                            <h3 className="text-lg font-black tracking-tight text-zinc-800 dark:text-zinc-100 leading-none">
                                {loadingStats ? '...' : `${stats.averageAttendance}%`}
                            </h3>
                        </div>
                    </div>

                    {/* Card 3 */}
                    <div className="p-4 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 shadow-sm flex items-center gap-4 transition-all hover:border-zinc-300 dark:hover:border-zinc-700">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            stats.riskCount > 0 
                                ? 'bg-red-500/10 text-red-600 dark:text-red-400' 
                                : 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400'
                        }`}>
                            <ShieldAlert className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 leading-none mb-1">At Risk (&lt;75%)</p>
                            <h3 className={`text-lg font-black tracking-tight leading-none ${stats.riskCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-zinc-800 dark:text-zinc-100'}`}>
                                {loadingStats ? '...' : stats.riskCount}
                            </h3>
                        </div>
                    </div>

                    {/* Card 4 */}
                    <div className="p-4 rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200/80 dark:border-zinc-800/80 shadow-sm flex items-center gap-4 transition-all hover:border-zinc-300 dark:hover:border-zinc-700">
                        <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center text-violet-600 dark:text-violet-400">
                            <Clock className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 leading-none mb-1">Today's Sessions</p>
                            <h3 className="text-lg font-black tracking-tight text-zinc-800 dark:text-zinc-100 leading-none">
                                {loadingStats ? '...' : stats.todayClasses}
                            </h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Active Panel View */}
            <div className="flex-1 overflow-hidden relative">
                <AnimatePresence mode="wait">
                    {activeTab === 'students' ? (
                        <motion.div
                            key="students"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="w-full h-full overflow-hidden"
                        >
                            <StudentsPanel />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="calendar"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.15 }}
                            className="w-full h-full overflow-hidden"
                        >
                            <CalendarPanel />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}
